import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, SlidersHorizontal, List, Star, LayoutGrid, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Separator } from './ui/separator';
import { ProductCard } from './ProductCard';
import { Product } from '../types';
import { useProducts } from '../hooks/useProducts';

interface CategoryPageProps {
  searchQuery: string;
}

const categoryInfo = {
  'gold': {
    title: 'Gold Collection',
    subtitle: 'Premium gold jewellery crafted with finest quality',
    description: 'Explore our exquisite range of gold jewellery featuring traditional and contemporary designs.',
    totalItems: 89,
    bgColor: 'bg-gradient-to-br from-brand-gold/10 to-brand-green/5'
  },
  'silver': {
    title: 'Silver Collection',
    subtitle: 'Elegant silver jewellery for every occasion',
    description: 'Discover our beautiful silver collection with modern designs and timeless classics.',
    totalItems: 67,
    bgColor: 'bg-gradient-to-br from-gray-100 to-brand-green/5'
  },
  'gold-coins': {
    title: 'Gold Coins',
    subtitle: 'Investment grade gold coins',
    description: 'Pure gold coins perfect for investment and gifting purposes.',
    totalItems: 23,
    bgColor: 'bg-gradient-to-br from-brand-gold/10 to-brand-green/5'
  },
  'silver-coins': {
    title: 'Silver Coins',
    subtitle: 'Premium silver coins collection',
    description: 'High-quality silver coins for collectors and investors.',
    totalItems: 15,
    bgColor: 'bg-gradient-to-br from-gray-100 to-brand-green/5'
  },
  'silver-gifts': {
    title: 'Silver Gifts',
    subtitle: 'Perfect silver gifts for special occasions',
    description: 'Beautiful silver gift items for festivals, weddings, and celebrations.',
    totalItems: 28,
    bgColor: 'bg-gradient-to-br from-gray-100 to-brand-green/5'
  }
};

export function CategoryPage({ searchQuery }: CategoryPageProps) {
  const navigate = useNavigate();
  const { slug: category } = useParams<{ slug: string }>();
  const { products: allProducts, loading } = useProducts(category || '', searchQuery);
  const [sortBy, setSortBy] = useState('featured');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const info = categoryInfo[category as keyof typeof categoryInfo] || {
    title: category ? (category.charAt(0).toUpperCase() + category.slice(1)) : 'Collection',
    subtitle: 'Premium jewellery collection',
    description: 'Explore our curated collection of fine jewellery.',
    totalItems: allProducts.length,
    bgColor: 'bg-gradient-to-br from-brand-green/10 to-brand-gold/5'
  };

  const sortedProducts = useMemo(() => {
    let filtered = [...allProducts];

    switch (sortBy) {
      case 'price-low':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'rating':
        filtered.sort((a, b) => b.rating - a.rating);
        break;
      case 'newest':
        filtered.sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0));
        break;
      default:
        filtered.sort((a, b) => {
          if (a.isBestseller && !b.isBestseller) return -1;
          if (!a.isBestseller && b.isBestseller) return 1;
          return b.rating - a.rating;
        });
    }

    return filtered;
  }, [allProducts, sortBy]);

  return (
    <div className="min-h-screen bg-focused-bg transition-colors duration-300">
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-border">
        <div className="flex items-center justify-between px-3 sm:px-6 py-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="text-brand-green hover:bg-brand-green/10"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>

          <div className="flex items-center space-x-2">
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-40 border-brand-green/30">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="featured">Featured</SelectItem>
                <SelectItem value="price-low">Price: Low to High</SelectItem>
                <SelectItem value="price-high">Price: High to Low</SelectItem>
                <SelectItem value="rating">Highest Rated</SelectItem>
                <SelectItem value="newest">Newest First</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex border border-border rounded-lg">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className={viewMode === 'grid' ? 'bg-brand-green hover:bg-brand-green/90' : 'hover:bg-brand-green/10'}
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className={viewMode === 'list' ? 'bg-brand-green hover:bg-brand-green/90' : 'hover:bg-brand-green/10'}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-6 sm:py-8">
        <div className="flex items-center gap-3 mb-6">
          <Badge className="bg-brand-green hover:bg-brand-green/90 text-white px-3 py-1 text-sm">
            {loading ? '...' : `${sortedProducts.length} Products`}
          </Badge>
          <h1 className="text-2xl font-light text-brand-green">{info.title}</h1>
        </div>

        <Separator className="mb-8" />

        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 bg-white/50 rounded-2xl border border-dashed border-brand-green/20">
            <Loader2 className="h-12 w-12 text-brand-green animate-spin mb-4" />
            <p className="text-brand-green font-medium">Loading collection...</p>
          </div>
        ) : sortedProducts.length > 0 ? (
          viewMode === 'grid' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {sortedProducts.map((product) => (
                <ProductCard 
                  key={product.id} 
                  product={product} 
                  isFavorite={false}
                  onToggleFavorite={() => {}}
                  onAddToCart={() => {}}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {sortedProducts.map((product) => (
                <Card
                  key={product.id}
                  className="cursor-pointer hover:shadow-lg transition-all duration-300 hover:border-brand-green/30"
                  onClick={() => navigate(`/product/${product.slug}`)}
                >
                  <CardContent className="p-6">
                    <div className="flex space-x-6">
                      <div className="w-32 h-32 flex-shrink-0">
                        <img
                          src={product.images && product.images.length > 0 ? product.images[0] : ''}
                          alt={product.name}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      </div>
                      <div className="flex-1 space-y-2">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="text-lg">{product.name}</h3>
                            <div className="flex items-center space-x-2 mt-1">
                              <div className="flex items-center space-x-1">
                                <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                                <span className="text-sm">{product.rating}</span>
                                <span className="text-sm text-muted-foreground">({product.reviewCount})</span>
                              </div>
                              {product.weight && (
                                <Badge variant="outline" className="border-brand-green/30 text-brand-green">
                                  {product.weight}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-xl text-brand-green">₹{product.price.toLocaleString()}</div>
                            {product.originalPrice && (
                              <div className="text-sm text-muted-foreground line-through">
                                ₹{product.originalPrice.toLocaleString()}
                              </div>
                            )}
                          </div>
                        </div>

                        <p className="text-muted-foreground text-sm line-clamp-2">
                          Premium {product.category} jewellery piece crafted with traditional techniques.
                        </p>

                        <div className="flex items-center justify-between pt-2">
                          <div className="flex space-x-2">
                            {product.isNew && (
                              <Badge className="bg-brand-green hover:bg-brand-green/90 text-white">
                                New
                              </Badge>
                            )}
                            {product.isBestseller && (
                              <Badge variant="secondary" className="bg-brand-gold/20 text-brand-gold border-brand-gold/30">
                                Bestseller
                              </Badge>
                            )}
                          </div>

                          <div className="flex space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                // onAddToCart(product);
                              }}
                              className="border-brand-green text-brand-green hover:bg-brand-green hover:text-white"
                            >
                              Add to Cart
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )
        ) : (
          <div className="text-center py-16">
            <SlidersHorizontal className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg mb-2">No products found</h3>
            <p className="text-muted-foreground">Try adjusting your filters or browse other categories</p>
          </div>
        )}

        {/* Load More */}
        {sortedProducts.length > 0 && (
          <div className="text-center mt-12">
            <Button
              variant="outline"
              className="border-brand-green text-brand-green hover:bg-brand-green/10"
            >
              Load More Products
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}